import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  IconButton,
  Box,
  Divider,
  useToast,
  Button,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useColorModeValue,
  Switch,
  Icon,
  Code,
  Link,
  Alert,
  AlertIcon,
  Select,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiVideo,
  FiUsers,
  FiClock,
  FiExternalLink,
} from 'react-icons/fi';
import { SiGooglemeet, SiZoom } from 'react-icons/si';
import moment from 'moment';

export default function EventDetailDrawer({ 
  isOpen, 
  onClose, 
  event,
  onUpdate,
  onDelete,
}) {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedDuration, setEditedDuration] = useState('30');
  const [editedAllDay, setEditedAllDay] = useState(false);
  const [editedAttendees, setEditedAttendees] = useState('');

  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBG = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const infoBG = useColorModeValue('gray.50', 'gray.700');

  // Initialize edit state when event changes
  useEffect(() => {
    if (event) {
      setEditedTitle(event.title || '');
      setEditedDescription(event.description || '');
      setEditedAllDay(event.allDay || false);
      
      const startTime = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
      const endTime = event.endTime?.toDate ? event.endTime.toDate() : new Date(event.endTime);
      
      setEditedStartDate(moment(startTime).format('YYYY-MM-DD'));
      setEditedStartTime(moment(startTime).format('HH:mm'));
      
      const durationMinutes = moment(endTime).diff(moment(startTime), 'minutes');
      setEditedDuration(durationMinutes.toString());
      
      setEditedAttendees((event.attendees || []).join(', '));
    }
  }, [event]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let startDateTime, endDateTime;

      if (editedAllDay) {
        startDateTime = moment(editedStartDate).startOf('day').toDate();
        endDateTime = moment(editedStartDate).endOf('day').toDate();
      } else {
        startDateTime = moment(`${editedStartDate} ${editedStartTime}`, 'YYYY-MM-DD HH:mm').toDate();
        endDateTime = moment(startDateTime).add(parseInt(editedDuration), 'minutes').toDate();
      }

      await onUpdate(event.id, {
        title: editedTitle,
        description: editedDescription,
        startTime: startDateTime,
        endTime: endDateTime,
        allDay: editedAllDay,
        attendees: editedAttendees ? editedAttendees.split(',').map(e => e.trim()).filter(e => e) : [],
      });

      toast({
        title: 'Event updated',
        status: 'success',
        duration: 2000,
      });

      setIsEditing(false);
    } catch (error) {
      toast({
        title: 'Error updating event',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this event? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(event.id);

      toast({
        title: 'Event deleted',
        status: 'success',
        duration: 2000,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Error deleting event',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!event) return null;

  const startTime = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
  const endTime = event.endTime?.toDate ? event.endTime.toDate() : new Date(event.endTime);
  const durationMinutes = moment(endTime).diff(moment(startTime), 'minutes');

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton zIndex={10} top="24px" right="24px" />
        
        <DrawerHeader bg={headerBG} pb={4} pt={6}>
          <VStack align="stretch" spacing={3}>
            {isEditing ? (
              <Input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                fontSize="lg"
                fontWeight="bold"
                bg="white"
              />
            ) : (
              <Heading size="md">{event.title}</Heading>
            )}

            <HStack spacing={2} flexWrap="wrap">
              <Badge colorScheme={event.allDay ? 'purple' : 'blue'} fontSize="xs">
                {event.allDay ? 'All Day' : 'Timed Event'}
              </Badge>
              
              {event.syncStatus === 'pending' && (
                <Badge colorScheme="orange" fontSize="xs">
                  Syncing to Google...
                </Badge>
              )}

              {event.syncStatus === 'error' && (
                <Badge colorScheme="red" fontSize="xs">
                  Sync Error
                </Badge>
              )}

              {event.syncedWithGoogle && (
                <Badge colorScheme="green" fontSize="xs">
                  <HStack spacing={1}>
                    <Icon as={FiCheckCircle} boxSize={2} />
                    <Text>Synced</Text>
                  </HStack>
                </Badge>
              )}

              {event.origin === 'google' && (
                <Badge colorScheme="gray" fontSize="xs">
                  From Google
                </Badge>
              )}

              {(event.googleMeetLink || event.zoomMeetingLink) && (
                <Badge colorScheme="cyan" fontSize="xs">
                  <Icon as={FiVideo} boxSize={2} mr={1} />
                  Video
                </Badge>
              )}
            </HStack>
          </VStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={5}>
            {/* Date/Time Section */}
            <Box>
              <HStack spacing={2} mb={3}>
                <Icon as={FiClock} />
                <Text fontWeight="semibold" fontSize="sm">
                  When
                </Text>
              </HStack>
              
              {isEditing ? (
                <VStack align="stretch" spacing={3}>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel fontSize="sm" mb={0}>All Day Event</FormLabel>
                    <Switch
                      isChecked={editedAllDay}
                      onChange={(e) => setEditedAllDay(e.target.checked)}
                      colorScheme="blue"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="xs">Date</FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={editedStartDate}
                      onChange={(e) => setEditedStartDate(e.target.value)}
                    />
                  </FormControl>

                  {!editedAllDay && (
                    <>
                      <FormControl>
                        <FormLabel fontSize="xs">Time</FormLabel>
                        <Input
                          type="time"
                          size="sm"
                          value={editedStartTime}
                          onChange={(e) => setEditedStartTime(e.target.value)}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="xs">Duration</FormLabel>
                        <Select
                          size="sm"
                          value={editedDuration}
                          onChange={(e) => setEditedDuration(e.target.value)}
                        >
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="45">45 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="90">1.5 hours</option>
                          <option value="120">2 hours</option>
                        </Select>
                      </FormControl>
                    </>
                  )}
                </VStack>
              ) : (
                <Box p={3} bg={infoBG} borderRadius="md">
                  <Text fontSize="sm" fontWeight="semibold">
                    {moment(startTime).format('dddd, MMMM Do, YYYY')}
                  </Text>
                  {!event.allDay && (
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {moment(startTime).format('h:mm A')} - {moment(endTime).format('h:mm A')} ({durationMinutes} min)
                    </Text>
                  )}
                </Box>
              )}
            </Box>

            <Divider />

            {/* Description */}
            {(event.description || isEditing) && (
              <>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    Description
                  </Text>
                  {isEditing ? (
                    <Textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      rows={4}
                      size="sm"
                      placeholder="Event details..."
                    />
                  ) : (
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {event.description || 'No description'}
                    </Text>
                  )}
                </Box>
                <Divider />
              </>
            )}

            {/* Attendees */}
            {(event.attendees?.length > 0 || isEditing) && (
              <>
                <Box>
                  <HStack spacing={2} mb={2}>
                    <Icon as={FiUsers} />
                    <Text fontWeight="semibold" fontSize="sm">
                      Attendees
                    </Text>
                  </HStack>
                  
                  {isEditing ? (
                    <Input
                      value={editedAttendees}
                      onChange={(e) => setEditedAttendees(e.target.value)}
                      placeholder="email1@example.com, email2@example.com"
                      size="sm"
                    />
                  ) : (
                    <VStack align="flex-start" spacing={1}>
                      {event.attendees.map((email, index) => (
                        <Text key={index} fontSize="sm" color="gray.700">
                          • {email}
                        </Text>
                      ))}
                    </VStack>
                  )}
                </Box>
                <Divider />
              </>
            )}

            {/* Video Link */}
            {(event.googleMeetLink || event.zoomMeetingLink) && !isEditing && (
              <>
                <Box>
                  <HStack spacing={2} mb={2}>
                    <Icon as={FiVideo} />
                    <Text fontWeight="semibold" fontSize="sm">
                      Video Meeting
                    </Text>
                  </HStack>
                  
                  {event.googleMeetLink && (
                    <Link href={event.googleMeetLink} isExternal>
                      <Button
                        leftIcon={<SiGooglemeet />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        rightIcon={<FiExternalLink />}
                      >
                        Join Google Meet
                      </Button>
                    </Link>
                  )}
                  
                  {event.zoomMeetingLink && (
                    <Link href={event.zoomMeetingLink} isExternal>
                      <Button
                        leftIcon={<SiZoom />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        rightIcon={<FiExternalLink />}
                      >
                        Join Zoom Meeting
                      </Button>
                    </Link>
                  )}
                </Box>
                <Divider />
              </>
            )}

            {/* Related Deal */}
            {event.relatedTo && (
              <>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    Related To
                  </Text>
                  <Box p={3} bg="purple.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="medium">
                      {event.relatedTo.name}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Deal ID: <Code fontSize="xs">{event.relatedTo.id}</Code>
                    </Text>
                  </Box>
                </Box>
                <Divider />
              </>
            )}

            {/* Google Calendar Sync Status */}
            {event.googleCalendarEventId && !isEditing && (
              <Alert status="success" size="sm" borderRadius="md">
                <AlertIcon />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontSize="xs" fontWeight="semibold">
                    Synced with Google Calendar
                  </Text>
                  {event.googleHtmlLink && (
                    <Link href={event.googleHtmlLink} isExternal fontSize="xs" color="blue.600">
                      Open in Google Calendar <Icon as={FiExternalLink} boxSize={2} />
                    </Link>
                  )}
                </VStack>
              </Alert>
            )}

            {event.syncStatus === 'error' && !isEditing && (
              <Alert status="error" size="sm" borderRadius="md">
                <AlertIcon />
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="xs" fontWeight="semibold">
                    Google Calendar sync failed
                  </Text>
                  <Text fontSize="2xs" color="gray.600">
                    {event.syncError || 'Will retry automatically'}
                  </Text>
                </VStack>
              </Alert>
            )}

            {/* Action Buttons */}
            <HStack spacing={2}>
              {isEditing ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<FiCheckCircle />}
                    onClick={handleSave}
                    isLoading={isSaving}
                    loadingText="Saving..."
                  >
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    leftIcon={<FiEdit2 />}
                    onClick={() => setIsEditing(true)}
                    flex={1}
                  >
                    Edit Event
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<FiTrash2 />}
                    colorScheme="red"
                    variant="outline"
                    onClick={handleDelete}
                    isLoading={isDeleting}
                  >
                    Delete
                  </Button>
                </>
              )}
            </HStack>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

